import {
  MenuRow,
  SettingCard,
  SettingCardRow,
} from '@components/Dashboard/components'
import type { AccountFormHandler } from '@components/Dashboard/pages/Account/types'
import { Switch } from '@lib/components'
import type { AccountData } from '@store/reducers/account'
import type React from 'react'

import type { NotifyComments, NotifyFrequency } from '@/types'
import { Divider } from '../../../../components/Divider'

interface Props {
  state: AccountData
  onChange: AccountFormHandler
}

// The inbox rows and the emails are built by octopus-api (OCT-5229); these
// settings are what it reads for the person.
const commentsOptions: { value: NotifyComments; label: string }[] = [
  { value: 'all', label: 'All comments' },
  { value: 'mentions', label: 'Mentions and replies' },
  { value: 'none', label: 'None' },
]

const frequencyOptions: { value: NotifyFrequency; label: string }[] = [
  { value: 'instant', label: 'Instantly' },
  { value: '30m', label: 'Every 30 minutes' },
  { value: 'daily', label: 'Once a day' },
]

const pad = (hour: number) => `${hour}`.padStart(2, '0')

const hourOptions = Array.from({ length: 24 }, (_, hour) => ({
  value: `${hour}`,
  label: `${pad(hour)}:00`,
}))

const isNotifyComments = (value: string): value is NotifyComments =>
  commentsOptions.some((option) => option.value === value)

const isNotifyFrequency = (value: string): value is NotifyFrequency =>
  frequencyOptions.some((option) => option.value === value)

export const NotificationsSection: React.FC<Props> = ({ state, onChange }) => {
  const handleComments = (value: string) => {
    if (isNotifyComments(value)) {
      onChange('notifyComments')(value)
    }
  }

  const handleFrequency = (value: string) => {
    if (isNotifyFrequency(value)) {
      onChange('notifyFrequency')(value)
    }
  }

  const handleHour = (value: string) => {
    onChange('notifyDailyHour')(Number(value))
  }

  return (
    <SettingCard title="Notifications">
      <MenuRow
        label="Comments"
        description="Projects you take part in"
        value={state.notifyComments}
        options={commentsOptions}
        onChange={handleComments}
      />

      <Divider />

      <MenuRow
        label="Send them"
        description="Mentions and replies to you are always sent instantly"
        value={state.notifyFrequency}
        options={frequencyOptions}
        onChange={handleFrequency}
      />

      <Divider />

      <SettingCardRow title="Daily summary of changes in projects" titleCenter>
        <Switch
          checked={state.notifyProjectDigest}
          onChange={onChange('notifyProjectDigest')}
        />
      </SettingCardRow>

      <Divider />

      <MenuRow
        label="Daily emails at"
        description={`Your time zone: ${state.timezone || 'UTC'}`}
        value={`${state.notifyDailyHour}`}
        options={hourOptions}
        onChange={handleHour}
      />
    </SettingCard>
  )
}
